import React, { useState, useEffect } from "react";
import { Table, Button, Input, Popconfirm, Form } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

const DataTable = ({ onDataChange, refreshTrigger }) => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modified to fetch data only when refreshTrigger changes
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const response = await fetch("https://api.repo.tradingmechanism.com/get_table_data");
        if (response.ok) {
          const csvData = await response.json();

          const transformedData = csvData.map((row) => ({
            key: row.key,
            firmId: row.firmId,
            counterpartyId: row.counterpartyId,
            firstLegPrice: row.firstLegPrice,
            secondLegPrice: row.secondLegPrice,
            collateralUnits: row.collateralUnits,
          }));

          setData(transformedData);

          // Initialize form with the data
          form.setFieldsValue({
            rows: transformedData.reduce((acc, row) => {
              acc[row.key] = row;
              return acc;
            }, {}),
          });
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
      setLoading(false);
    };

    fetchInitialData();
  }, [form, refreshTrigger]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      console.log("Form values:", values);

      // Extract the updated data from form values
      const updatedData = Object.entries(values.rows).map(([key, value]) => ({
        key,
        ...value,
      }));

      // Update the data state
      setData(updatedData);

      // Send the updated data to the parent component
      onDataChange(updatedData);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
    }
  };

  const handleDelete = (key) => {
    const newData = data.filter((item) => item.key !== key);
    setData(newData);

    // Also update the form data
    const currentRows = form.getFieldValue("rows") || {};
    const newRows = { ...currentRows };
    delete newRows[key];
    form.setFieldsValue({ rows: newRows });
  };

  const handleAdd = () => {
    if (data.length >= 20) {
      alert("Maximum 20 rows allowed");
      return;
    }

    const newKey = Date.now().toString();
    const newRow = {
      key: newKey,
      firmId: "",
      counterpartyId: "",
      firstLegPrice: "",
      secondLegPrice: "",
      collateralUnits: "",
    };

    // Add to data state
    setData([...data, newRow]);

    // Also add to form data
    const currentRows = form.getFieldValue("rows") || {};
    form.setFieldsValue({
      rows: {
        ...currentRows,
        [newKey]: newRow,
      },
    });
  };

  const handleFieldChange = (record, fieldName, value) => {
    // This is a utility function that could be used for direct field updates
    // if you want to track changes immediately without waiting for form submission
    const newData = [...data];
    const index = newData.findIndex((item) => item.key === record.key);
    if (index > -1) {
      newData[index][fieldName] = value;
      setData(newData);
    }
  };

  const columns = [
    {
      title: "Firm Identifier",
      dataIndex: "firmId",
      width: "20%",
      render: (text, record) => (
        <Form.Item
          name={["rows", record.key, "firmId"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Required" }]}
        >
          <Input
            bordered={false}
            style={{
              width: "100%",
              textAlign: "center",
              padding: "4px 8px",
            }}
          />
        </Form.Item>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
        },
      }),
    },
    {
      title: "Counterparty Identifier",
      dataIndex: "counterpartyId",
      width: "20%",
      render: (text, record) => (
        <Form.Item
          name={["rows", record.key, "counterpartyId"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Required" }]}
        >
          <Input
            bordered={false}
            style={{
              width: "100%",
              textAlign: "center",
              padding: "4px 8px",
            }}
          />
        </Form.Item>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
        },
      }),
    },
    {
      title: "First Leg Price",
      dataIndex: "firstLegPrice",
      width: "20%",
      render: (text, record) => (
        <Form.Item
          name={["rows", record.key, "firstLegPrice"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Required" }]}
        >
          <Input
            bordered={false}
            style={{
              width: "100%",
              textAlign: "center",
              padding: "4px 8px",
            }}
          />
        </Form.Item>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
        },
      }),
    },
    {
      title: "Second Leg Price",
      dataIndex: "secondLegPrice",
      width: "20%",
      render: (text, record) => (
        <Form.Item
          name={["rows", record.key, "secondLegPrice"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Required" }]}
        >
          <Input
            bordered={false}
            style={{
              width: "100%",
              textAlign: "center",
              padding: "4px 8px",
            }}
          />
        </Form.Item>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
        },
      }),
    },
    {
      title: "Collateral Units",
      dataIndex: "collateralUnits",
      width: "15%",
      render: (text, record) => (
        <Form.Item
          name={["rows", record.key, "collateralUnits"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Required" }]}
        >
          <Input
            bordered={false}
            style={{
              width: "100%",
              textAlign: "center",
              padding: "4px 8px",
            }}
          />
        </Form.Item>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
        },
      }),
    },
    {
      title: "",
      width: "5%",
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this row?"
          onConfirm={() => handleDelete(record.key)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
        },
      }),
    },
  ];

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "24px",
      }}
    >
      <Form form={form} component={false}>
        <Table
          bordered
          dataSource={data}
          columns={columns}
          rowClassName="compact-row"
          loading={loading}
          pagination={false}
          size="small"
        />
        <div
          style={{
            marginTop: "16px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            type="dashed"
            onClick={handleAdd}
            icon={<PlusOutlined />}
            disabled={data.length >= 20}
          >
            Add Row
          </Button>
          <Button type="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DataTable;
